import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    linkedSignal,
    model,
    Signal,
    signal,
    TemplateRef,
    untracked,
    viewChild,
    ViewEncapsulation,
    WritableSignal,
} from "@angular/core";
import { Grid } from "./s25.virtual.grid";
import { S25VirtualGridComponent } from "./s25.virtual.grid.component";
import { Bind } from "../../decorators/bind.decorator";
import { Merge, Nullable } from "../../pojo/Util";
import { Proto } from "../../pojo/Proto";
import { S25Util } from "../../util/s25-util";
import { MultiselectModelI } from "../s25-multiselect/s25.multiselect.component";
import { DowGridUtil } from "./s25.dow.grid.util";
import { DropDownItem } from "../../pojo/DropDownItem";
import { StandardSchedule, StandardScheduleService } from "../../services/standard.schedule.service";
import { s25Resource } from "../../util/s25.resource";
import { UserStore } from "../../stores/user.store";
import ColumnType = DowGrid.ColumnType;
@Component({
    selector: "s25-ng-dow-pattern-grid",
    template: `
        @if (isLoading()) {
            <s25-ng-loading-inline-static />
        }
        <s25-ng-virtual-grid
            [items]="items()"
            [columnHeaders]="columnHeaders()"
            [rowHeaders]="rowHeaders()"
            [displayMessage]="displayMessage()"
            [interactionHandlers]="dowInteractionHandlers"
            [domainEquals]="domainEquals()"
            [dragParticipantsResolver]="dragParticipantsResolver()"
            [itemInitialized]="initializeItemPost"
            [itemProjector]="dowItemProjector"
            [cornerTemplate]="cornerTmpl()"
            [columnHeaderTemplate]="columnHeaderTmpl()"
            [rowHeaderTemplate]="rowHeaderTemplate()"
            [itemTemplate]="itemTemplate()"
            [decorationTemplate]="dowDecorationTmpl()"
            [supplementalDecorations]="supplementalDecorations()"
            [optionsLeftTemplate]="optionsLeftTmpl()"
            [optionsMiddleTemplate]="optionsMiddleTemplate()"
            [optionsRightTemplate]="optionsRightTmpl()"
            [optionsBelowTemplate]="optionsBelowTemplate()"
            [canDrag]="canDrag()"
            [hasMinimap]="hasMinimap()"
            [hasUndo]="hasUndo()"
            [snapStep]="snapStep()"
            [findOverlap]="findOverlap()"
            [generateOverlapProjections]="createOverlapItems()"
            [isLoading]="isLoading()"
            [frozenRows]="frozenRows()"
            [style.--dows]="visibleDows().length || 1"
        />

        <ng-template #dowDecorationTemplate [template]="dowDecorationTmpl()" let-decoration="decoration">
            @if (decoration.data.type === "standard-schedule") {
                <div class="item grid-color schedule" data-type="standardSchedule"></div>
            } @else if (decorationTemplate(); as parentDecoration) {
                <ng-container
                    [ngTemplateOutlet]="parentDecoration"
                    [ngTemplateOutletContext]="{ decoration: decoration }"
                />
            } @else if (decoration.data.type === "aggregate-overlap") {
                <div class="overlapItem"></div>
            }
        </ng-template>

        <ng-template #corner void>
            <s25-ng-office-hours-slider [prefName]="false" [(start)]="startHour" [(end)]="endHour" />
        </ng-template>

        <ng-template #optionsLeft [template]="optionsLeftTmpl()" let-defaultOptions="defaultOptions">
            <ng-container
                [ngTemplateOutlet]="optionsLeftTemplate()"
                [ngTemplateOutletContext]="{ defaultOptions: leftDefaultsTmpl() }"
            />
            @if (!optionsLeftTemplate()) {
                <ng-container
                    [ngTemplateOutlet]="leftDefaultsTmpl()"
                    [ngTemplateOutletContext]="{ defaultOptions: defaultOptions }"
                />
            }
        </ng-template>

        <ng-template #leftDefaults [template]="leftDefaultsTmpl()" let-defaultOptions="defaultOptions">
            @if (hasVisibleDowSelector() && dowMultiselectModel()) {
                <s25-ng-multiselect-popup [modelBean]="dowMultiselectModel()" />
            }
            @if (hasStandardSchedule()) {
                <s25-ng-dropdown-search-criteria
                    [type]="'standardSchedules'"
                    [(value)]="standardSchedule"
                    [placeholder]="'Standard Schedules'"
                    [emptyText]="'No Standard Schedules'"
                    [hasNoneItem]="true"
                    [noneItemLabel]="'No Schedule'"
                    [ariaLabel]="'Select a standard schedule'"
                />
                @if (hasSnapToStandardSchedules() && !!canDrag()?.x && !!selectedSchedule.value()?.length) {
                    <s25-ng-checkbox [(value)]="snapToStandardSchedules" title="Snap to standard schedules">
                        Snap to Schedule
                    </s25-ng-checkbox>
                }
            }
            <ng-container [ngTemplateOutlet]="defaultOptions" />
        </ng-template>

        <ng-template #optionsRight [template]="optionsRightTmpl()" let-defaultOptions="defaultOptions">
            <ng-container
                [ngTemplateOutlet]="optionsRightTemplate()"
                [ngTemplateOutletContext]="{ defaultOptions: defaultOptions }"
            />
            @if (!optionsRightTemplate()) {
                <ng-container [ngTemplateOutlet]="defaultOptions" />
            }
        </ng-template>

        <ng-template #columnHeaderTemplate [template]="columnHeaderTmpl()" let-header="header">
            <div class="header" [class.tick]="header.dowGrid.tick">{{ header.heading }}</div>
        </ng-template>
    `,
    styles: `
        :host {
            --grid-column-width: var(--column-width, 150px);
        }

        :host ::ng-deep .grid--column-line[data-truncate-boundary],
        :host ::ng-deep .grid--column-header:not(.leaf)::before,
        :host ::ng-deep .grid--column-headers--sub-headers .leaf:first-child {
            --column-line-width: 2px;
        }

        ::ng-deep s25-ng-dow-pattern-grid .grid--column-header.leaf:not(:has(.tick)) {
            background: none;
        }

        ::ng-deep s25-ng-dow-pattern-grid .grid--column-header.leaf:not(:first-child, :has(.tick))::before {
            border-color: transparent;
        }

        ::ng-deep s25-ng-dow-pattern-grid s25-ng-virtual-grid .minimap {
            --rule-width: 1px;
            --rule-color: color-mix(in oklab, var(--background) 100%, white 17%);
            --dow-width: calc(100% / var(--dows) - var(--rule-width));
            background-image: repeating-linear-gradient(
                to right,
                var(--background),
                var(--background) var(--dow-width),
                var(--rule-color) var(--dow-width),
                var(--rule-color) calc(var(--dow-width) + var(--rule-width))
            ) !important;
            background-size: calc(100% + var(--rule-width)) !important;
        }

        .schedule {
            height: 100%;
        }

        .overlapItem {
            border: 2px dashed #03f;
            height: 100%;
            z-index: 10;
            position: relative;
        }

        .header {
            padding-inline: 0.5rem;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export class S25DowGridComponent<
    RowType extends DowGrid.SourceHeader = DowGrid.SourceHeader,
    ItemType extends DowGrid.SourceItem = DowGrid.SourceItem,
    ProjectionData extends DowGrid.ProjectionData = DowGrid.ProjectionData,
    DecorationData extends DowGrid.CustomData = DowGrid.DecorationData,
> {
    // IO
    readonly itemsInput = input<ItemType[]>([], { alias: "items" });
    readonly rowHeaders = input<RowType[]>([]);
    readonly displayMessage = input<string>();
    readonly interactionHandlers = input<DowGrid.InteractionHandlers<ItemType> | undefined>(undefined);
    readonly domainEquals = input.required<DowGrid.DomainEquals<Grid.PositionOf<ItemType>>>();
    readonly itemInitialized = input<(item: DowGrid.Item<ItemType>) => void>();
    readonly isLoading = input(false);
    readonly itemProjector = input.required<DowGrid.ItemProjector<ItemType, ProjectionData>>();
    readonly startHour = model.required<Proto.Integer>();
    readonly endHour = model.required<Proto.Integer>();
    readonly dowsInput = input<string[]>(undefined, { alias: "dows" });
    readonly visibleDowsInput = model<string[]>([], { alias: "visibleDows" });
    readonly hasVisibleDowSelector = input(true);
    readonly displayShadows = input(false);
    readonly rowHeaderTemplate = input<TemplateRef<DowGrid.RowHeaderTemplateContext<RowType>>>();
    readonly itemTemplate = input<TemplateRef<DowGrid.ItemProjectionTemplateContext<ItemType, ProjectionData>>>();
    readonly decorationTemplate =
        input<TemplateRef<DowGrid.DecorationTemplateContext<ItemType, ProjectionData, DecorationData>>>();
    readonly optionsLeftTemplate = input<TemplateRef<DowGrid.WrappedOptionsTemplateContext>>();
    readonly optionsMiddleTemplate = input<TemplateRef<DowGrid.OptionsTemplateContext>>();
    readonly optionsRightTemplate = input<TemplateRef<DowGrid.OptionsTemplateContext>>();
    readonly optionsBelowTemplate = input<TemplateRef<void>>();
    readonly canDrag = input<{ x: boolean; y: boolean }>();
    readonly hasMinimap = input(false);
    readonly hasUndo = input(false);
    readonly snapStep = input(S25DowGridComponent.DEFAULT_SNAP_STEP, {
        transform: (step: Nullable<Grid.Coord>): Grid.Coord => step ?? S25DowGridComponent.DEFAULT_SNAP_STEP,
    });
    readonly dragParticipantsResolver = input<DowGrid.DragParticipantsResolver<ItemType>>();
    readonly spanHours = input(1);
    readonly hasStandardSchedule = input(false);
    readonly standardSchedule = model<DropDownItem<number>>();
    readonly selectedScheduleInput = model<StandardSchedule[] | undefined>(undefined, { alias: "selectedSchedule" });
    readonly hasSnapToStandardSchedules = input(false);
    readonly snapToStandardSchedules = model(false);
    readonly findOverlap = input(false);
    readonly createOverlapItems = input(false);
    readonly resizeOnDowChange = input(false);
    readonly frozenRows = input(0);

    // Views
    private readonly virtualGridRef =
        viewChild<
            S25VirtualGridComponent<DowGrid.ColumnType, RowType, DowGrid.Item<ItemType>, ProjectionData, DecorationData>
        >(S25VirtualGridComponent);
    protected readonly dowDecorationTmpl = viewChild<
        string,
        TemplateRef<DowGrid.DecorationTemplateContext<ItemType, ProjectionData, DecorationData>>
    >("dowDecorationTemplate", { read: TemplateRef });
    protected readonly cornerTmpl = viewChild<string, TemplateRef<void>>("corner", { read: TemplateRef });
    protected readonly optionsLeftTmpl = viewChild<string, TemplateRef<DowGrid.OptionsTemplateContext>>("optionsLeft", {
        read: TemplateRef,
    });
    protected readonly leftDefaultsTmpl = viewChild<string, TemplateRef<DowGrid.OptionsTemplateContext>>(
        "leftDefaults",
        { read: TemplateRef },
    );
    protected readonly optionsRightTmpl = viewChild<string, TemplateRef<DowGrid.OptionsTemplateContext>>(
        "optionsRight",
        {
            read: TemplateRef,
        },
    );
    protected readonly columnHeaderTmpl = viewChild<
        string,
        TemplateRef<DowGrid.ColumnHeaderTemplateContext<ColumnType>>
    >("columnHeaderTemplate", {
        read: TemplateRef,
    });

    // Consts
    private static readonly DEFAULT_SNAP_STEP = { x: 5 / 60, y: 1 };
    readonly dowInteractionHandlers: Grid.InteractionHandlers<DowGrid.Item<ItemType>> = {
        onPickup: this.onPickup,
        onDragChanged: this.onDragChanged,
        onDrop: this.onDrop,
        onCancel: this.onCancel,
        onCommit: this.onCommit,
        onUndo: this.onUndo,
        onRedo: this.onRedo,
        beforeMinimapRender: this.beforeMinimapRender,
        afterMinimapRender: this.afterMinimapRender,
    };

    // Stores
    private readonly userStore = inject(UserStore);

    // Resources
    protected readonly selectedSchedule = s25Resource({
        if: () => this.hasStandardSchedule() && !!this.standardSchedule()?.itemId,
        params: () => ({
            standardSchedule: this.standardSchedule()!,
            selectedSchedule: this.selectedScheduleInput(),
        }),
        loader: async ({ params, abortSignal }) => {
            if (params.standardSchedule.itemId === params.selectedSchedule?.[0]?.schedule_id) {
                return params.selectedSchedule;
            }
            return StandardScheduleService.getStandardSchedule(params.standardSchedule.itemId!);
        },
    });

    // Computes
    private readonly itemDows = computed(() => {
        const items = this.items();
        return S25Util.array.unique(items.map((item) => item.position().dow));
    });
    private readonly allDows = computed(this.getAllDows, { equal: S25Util.array.allSame });
    protected readonly visibleDows = computed(this.getVisibleDows, { equal: S25Util.array.isEqual });
    private readonly dowColumnIndex = computed(() => new Map(this.allDows().map((dow, index) => [dow, index * 24])));
    /**
     * One proposal for every participant in the open drag session.
     * This is necessary for standard schedule snapping, which only snaps if all items can snap
     */
    private readonly draggedTimes = computed(() => {
        const session = this.virtualGridRef()?.dragSession();
        const offset = session?.dragOffset();
        if (!offset) return;
        return DowGridUtil.proposeDragPositions({
            participants: session!.participants,
            offset,
            dows: this.allDows(),
            dowColumnIndex: this.dowColumnIndex(),
            resizeOnDowChange: this.resizeOnDowChange(),
            schedule: this.schedulesByDow(),
            snapToStandardSchedules: this.snapToStandardSchedules(),
        });
    });

    protected readonly items = linkedSignal(() => {
        const items = this.itemsInput();
        return untracked(() => items.map((item) => this.initializeItemPre(item)).filter(S25Util.isDefined));
    });
    private readonly dowHeaders = computed(() => {
        return DowGridUtil.getHeaders(this.allDows(), this.userStore.is24Hours(), this.spanHours(), {
            visibleDows: this.visibleDows,
            startHour: this.startHour,
            endHour: this.endHour,
        });
    });
    protected readonly columnHeaders = computed(() => {
        return this.dowHeaders();
    });

    protected readonly scheduleBlocks = computed(() => {
        if (!this.hasStandardSchedule() || !this.standardSchedule()?.itemId) return undefined;
        return this.selectedSchedule.lastValue();
    });
    protected readonly supplementalDecorations = computed((): readonly Grid.SourceDecoration<DecorationData>[] => {
        return DowGridUtil.getStandardScheduleDecorations(this.scheduleBlocks(), {
            dowColumnIndex: this.dowColumnIndex(),
        }) as unknown as Grid.SourceDecoration<DecorationData>[];
    });
    private readonly schedulesByDow = computed(() => {
        const schedule = this.scheduleBlocks();
        if (!schedule?.length) return undefined;
        return <Record<string, StandardSchedule[]>>S25Util.array.groupBy(schedule, (schedule) => schedule.dow!);
    });

    protected readonly dowMultiselectModel = computed((): MultiselectModelI => {
        return {
            title: "Meeting Patterns",
            buttonText: "Meeting Patterns",
            hasSelectAll: true,
            hasSelectNone: true,
            usePopover: true,
            items: this.allDows().map((dow) => ({ itemId: dow, itemName: dow })),
            selectedItems: untracked(this.visibleDows).map((dow) => ({ itemId: dow, itemName: dow })),
            action: () => {
                if (!this.dowMultiselectModel().selectedItems!.length) {
                    return alert("Please select at least one meeting pattern");
                }
                this.visibleDowsInput.set(this.dowMultiselectModel().selectedItems!.map((item) => item.itemName!));
            },
        };
    });

    @Bind
    private getAllDows() {
        const itemDows = this.itemDows();
        let dows = S25Util.array.unique([...(this.dowsInput() || []), ...itemDows, ...this.visibleDows()]);
        if (!this.canDrag()?.x) {
            // If you can't change meeting patterns, include only meeting patterns with items
            const dataDowsSet = new Set(itemDows);
            dows = dows.filter((dow) => dataDowsSet.has(dow));
        }
        return dows;
    }

    @Bind
    private getVisibleDows() {
        let dows = this.visibleDowsInput();
        const itemDows = this.itemDows();
        if (!dows?.length) dows = itemDows;
        else if (!this.canDrag()?.x) {
            // If you can't change meeting patterns, include only meeting patterns with items
            const dataDowsSet = new Set(itemDows);
            dows = dows.filter((dow) => dataDowsSet.has(dow));
        }
        return dows;
    }

    @Bind
    protected dowItemProjector(item: DowGrid.Item<ItemType>): readonly Grid.ItemProjectionDefinition<ProjectionData>[] {
        let domainProjections = this.itemProjector()(item);

        if (this.displayShadows() && !item.dowGrid.noShadows?.()) {
            domainProjections = domainProjections.flatMap((projection) => {
                if (projection.noShadows?.()) return [projection];
                return [
                    projection,
                    ...DowGridUtil.shadowProjections(projection, {
                        dows: this.visibleDows(),
                        is24Hours: this.userStore.is24Hours(),
                    }),
                ];
            });
        }

        return domainProjections.map((definition) => this.mapDomainProjection(definition));
    }

    private mapDomainProjection(
        definition: DowGrid.ItemProjectionDefinition<ProjectionData>,
    ): Grid.ItemProjectionDefinition<ProjectionData> {
        return {
            ...definition,
            position: computed(() => ({
                ...DowGridUtil.timesToPosition(
                    {
                        dow: definition.data.dow(),
                        startHour: definition.data.startHour(),
                        endHour: definition.data.endHour(),
                    },
                    this.dowColumnIndex(),
                ),
                top: definition.data.top(),
                height: definition.data.height(),
            })),
        };
    }

    @Bind
    private initializeItemPre(item: DowGrid.UnInitializedItem<ItemType>): DowGrid.Item<ItemType> | undefined {
        const asInitialized = item as unknown as DowGrid.Item<ItemType>;
        if (!!item._dowGrid?.initialized?.()) return asInitialized;

        const initialized = signal<boolean>(false);
        const checkFitsSchedule = (times: DowGrid.Times) => {
            return DowGridUtil.fitsSchedule({
                schedulesByDow: this.schedulesByDow(),
                times,
                preHours: item.dowGrid.preHours?.(),
                postHours: item.dowGrid.postHours?.(),
            });
        };
        const candidatePosition = computed((): Grid.PositionOf<ItemType> | undefined => {
            const history = asInitialized.virtualGrid?.candidate.position();
            if (history) return history as Grid.PositionOf<ItemType>;
            const proposed = this.draggedTimes()?.get(asInitialized);
            if (!proposed) return;
            // Proposed times replace only dow, startHour, and endHour on the committed position.
            return { ...asInitialized.position(), ...proposed } as Grid.PositionOf<ItemType>;
        });
        asInitialized.dowGrid.candidate = {
            offset: computed(() => {
                const offset = asInitialized.virtualGrid?.candidate.offset();
                return offset && { y: offset.y };
            }),
            position: candidatePosition,
        };
        asInitialized.dowGrid.effective = computed(
            (): Grid.PositionOf<ItemType> =>
                candidatePosition() ?? (asInitialized.position() as Grid.PositionOf<ItemType>),
        );
        asInitialized.dowGrid.fitsSchedule = computed(() => checkFitsSchedule(asInitialized.dowGrid.effective()));

        item.ariaLabel ??= computed(() =>
            DowGridUtil.getAriaLabel(asInitialized.position(), this.userStore.is24Hours()),
        );

        item._dowGrid = {
            ...(item._dowGrid ?? {}),
            initialized,
        };
        return asInitialized;
    }

    @Bind
    protected initializeItemPost(item: DowGrid.Item<ItemType>) {
        item._dowGrid.initialized.set(true);
        this.itemInitialized()?.(item);
    }

    @Bind
    private async onPickup(context: Grid.PickupContext<DowGrid.Item<ItemType>>) {
        return (await this.interactionHandlers()?.onPickup?.(context)) ?? true;
    }

    @Bind
    private onDragChanged(context: Grid.DragChangedContext<DowGrid.Item<ItemType>>) {
        return this.interactionHandlers()?.onDragChanged?.(context);
    }

    @Bind
    private async onDrop(context: Grid.DropContext<DowGrid.Item<ItemType>>) {
        return (await this.interactionHandlers()?.onDrop?.(context)) ?? true;
    }

    @Bind
    private onCancel(context: Grid.DropContext<DowGrid.Item<ItemType>>): void {
        this.interactionHandlers()?.onCancel?.(context);
    }

    @Bind
    private onCommit(context: Grid.CommitContext<DowGrid.Item<ItemType>>): void {
        this.interactionHandlers()?.onCommit?.(context);
    }

    @Bind
    private async onUndo(context: Grid.HistoryContext<DowGrid.Item<ItemType>>) {
        return (await this.interactionHandlers()?.onUndo?.(context)) ?? true;
    }

    @Bind
    private async onRedo(context: Grid.HistoryContext<DowGrid.Item<ItemType>>) {
        return (await this.interactionHandlers()?.onRedo?.(context)) ?? true;
    }

    clearHistory() {
        this.virtualGridRef()?.clearHistory();
    }

    @Bind
    private beforeMinimapRender(context: CanvasRenderingContext2D, items: Grid.MinimapItem[]): void {
        this.interactionHandlers()?.beforeMinimapRender?.(context, items);
    }

    @Bind
    private afterMinimapRender(context: CanvasRenderingContext2D): void {
        this.interactionHandlers()?.afterMinimapRender?.(context);
    }

    public bringToFront(item?: DowGrid.Item<ItemType>) {
        this.virtualGridRef()?.bringToFront(item);
    }

    /** Animates the tile. The caller writes `item.position` to the destination. */
    public async moveItem(item: DowGrid.Item<ItemType>) {
        return this.virtualGridRef()?.moveItem(item);
    }
}

export namespace DowGrid {
    export type ColumnType = Grid.SourceHeader & { dowGrid: { dow: string; hour?: number; tick: boolean } };

    export type Times = {
        dow: string;
        startHour: number;
        endHour: number;
    };

    export function timesEqual(left: Times, right: Times): boolean {
        return left.dow === right.dow && left.startHour === right.startHour && left.endHour === right.endHour;
    }

    export type SnapOffset = { top: number; hours: number };

    export type SourceItem<Position extends Times = Times> = Grid.SourceItem<Position> & {
        dowGrid: {
            snapOffset?: Signal<SnapOffset>;
            unResizableHours?: Signal<number>;
            preHours?: Signal<number>;
            postHours?: Signal<number>;
            noShadows?: Signal<boolean>;
        };
    };

    type ItemInternals<ItemType extends SourceItem> = {
        dowGrid: ItemType["dowGrid"] & {
            candidate: {
                offset: Signal<{ y: number } | undefined>;
                position: Signal<Grid.PositionOf<ItemType> | undefined>;
            };
            fitsSchedule: Signal<boolean>;
            effective: Signal<Grid.PositionOf<ItemType>>;
        };
        _dowGrid: {
            initialized: WritableSignal<boolean>;
        };
    };

    export type Item<ItemType extends SourceItem = SourceItem> = Grid.Item<Merge<ItemType, ItemInternals<ItemType>>>;

    export type UnInitializedItem<ItemType extends SourceItem = SourceItem> = Merge<
        ItemType,
        {
            dowGrid: ItemType["dowGrid"] &
                Partial<Pick<ItemInternals<ItemType>["dowGrid"], "candidate" | "fitsSchedule" | "effective">>;
            _dowGrid?: Partial<ItemInternals<ItemType>["_dowGrid"]>;
        }
    >;

    export type ProjectionData = {
        dow: Signal<string>;
        startHour: Signal<number>;
        endHour: Signal<number>;
        top: Signal<number>;
        height: Signal<number>;
    };

    export type ScheduleDecorationData = {
        type: "standard-schedule";
        dow: string;
        blockId: number;
    };

    export type DecorationData = ScheduleDecorationData;

    export type InteractionHandlers<ItemType extends SourceItem> = Omit<
        Grid.InteractionHandlers<Item<ItemType>>,
        "onPickup"
    > & {
        onPickup?: (context: PickupContext<ItemType, CustomData>) => Promise<boolean>;
    };
    export type HistoryContext<ItemType extends SourceItem> = Grid.HistoryContext<Item<ItemType>>;
    export type DomainEquals<Position> = Grid.DomainEquals<Position>;
    export type DragParticipantsResolver<ItemType extends SourceItem> = Grid.DragParticipantsResolver<Item<ItemType>>;
    export type ItemProjector<ItemType extends SourceItem, ProjectionData extends DowGrid.ProjectionData> = (
        item: Item<ItemType>,
    ) => readonly ItemProjectionDefinition<ProjectionData>[];
    export type ItemProjectionTemplateContext<
        ItemType extends SourceItem = SourceItem,
        ProjectionData extends DowGrid.ProjectionData = DowGrid.ProjectionData,
    > = Grid.ItemProjectionTemplateContext<Item<ItemType>, ProjectionData | DowGrid.ProjectionData>;
    export type DecorationTemplateContext<
        ItemType extends SourceItem = SourceItem,
        ProjectionData extends DowGrid.ProjectionData = DowGrid.ProjectionData,
        DecorationData extends Grid.CustomData = Grid.CustomData,
    > = Grid.DecorationTemplateContext<Item<ItemType>, ProjectionData, DecorationData | DowGrid.DecorationData>;
    export type RowHeaderTemplateContext<RowType extends Grid.SourceHeader> = Grid.HeaderTemplateContext<RowType>;
    export type ColumnHeaderTemplateContext<ColumnType extends Grid.SourceHeader> =
        Grid.HeaderTemplateContext<ColumnType>;
    export type WrappedOptionsTemplateContext = { defaultOptions: TemplateRef<OptionsTemplateContext> };
    export type OptionsTemplateContext = Grid.OptionsTemplateContext;
    /** Parent-facing: domain coordinates live in `data`; the DOW grid maps them to `Grid.Rect`. */
    export type ItemProjectionDefinition<ProjectionData extends DowGrid.ProjectionData = DowGrid.ProjectionData> = Omit<
        Grid.ItemProjectionDefinition<ProjectionData>,
        "position"
    > & { noShadows?: Signal<boolean> };

    export type SourceHeader = Grid.SourceHeader;
    export type CustomData = Grid.CustomData;

    export type ItemProjection<
        ItemType extends SourceItem = SourceItem,
        ProjectionData extends CustomData = CustomData,
    > = Grid.ItemProjection<Item<ItemType>, ProjectionData>;

    export type PickupContext<ItemType extends SourceItem, ProjectionData extends CustomData> = Grid.PickupContext<
        Item<ItemType>,
        ProjectionData
    >;
    export type DragChangedContext<ItemType extends SourceItem> = Grid.DragChangedContext<Item<ItemType>>;
    export type DropContext<ItemType extends SourceItem> = Grid.DropContext<Item<ItemType>>;
    export type CommitContext<ItemType extends SourceItem> = DropContext<ItemType>;
    export type DropResult<ItemType extends SourceItem> = Grid.DropResult<Item<ItemType>>;
}
